import React from 'react';
import { ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { observer } from 'mobx-react';

import CancellableModal from '~/components/CancellableModal';
import BusyButton from '~/components/BusyButton';
import { SshKeysStore } from '~/lib/store/SshKeysStore';
import { SshKey } from '~/lib/ApiModel';
import { makeObservable, observable, runInAction } from 'mobx';
import SshKeyForm, { SshKeyFormStore } from './SshKeyForm';
import { tryGetAppContext } from '~/lib/AppContext';

type SshKeyEditModalProps = {
    store: SshKeysStore;
    sshKey?: SshKey;
    onClose: (updatedKey?: SshKey) => unknown;
};

type Mode = 'edit' | 'create';

@observer
export default class SshKeyEditModal extends React.Component<SshKeyEditModalProps> {
    @observable
    mode: Mode = 'create';

    form = new SshKeyFormStore();

    constructor(props: SshKeyEditModalProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        runInAction(() => {
            this.mode = this.props.sshKey ? 'edit' : 'create';
        });
        this.form.initFromSshKey(this.props.sshKey);
    }

    handleSubmit = () => {
        if (!this.form.validateForMode(this.mode)) return;
        void this.form.doSubmit(
            () => {
                return this.mode === 'create' ? this.doCreate() : this.doUpdate();
            },
            { errorSummary: 'Unable to save SSH key' }
        );
    };

    render() {
        const { onClose } = this.props;
        return (
            <CancellableModal
                isOpen
                toggle={() => onClose()}
                autoFocus={false}
                labelledBy="ssh-key-edit-modal-title"
                busy={this.form.isSubmitting}
            >
                <ModalHeader>
                    {/* id on the title span, not the ModalHeader — see PanelSite.md "Modals". */}
                    <span id="ssh-key-edit-modal-title">{this.mode === 'create' ? 'Add' : 'Edit'} SSH Key</span>
                </ModalHeader>
                <ModalBody>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            this.handleSubmit();
                        }}
                    >
                        <SshKeyForm store={this.form} mode={this.mode} />
                    </form>
                </ModalBody>
                <ModalFooter>
                    <BusyButton color="primary" busy={this.form.isSubmitting} onClick={this.handleSubmit}>
                        {this.mode === 'create' ? 'Create' : 'Update'}
                    </BusyButton>
                </ModalFooter>
            </CancellableModal>
        );
    }

    async doUpdate(): Promise<void> {
        if (!this.props.sshKey) return;
        const result = await this.props.store.updateSshKey(this.props.sshKey.id, this.form.values);
        tryGetAppContext()?.announce.fire('SSH key updated');
        this.props.onClose(result);
    }

    async doCreate(): Promise<void> {
        const result = await this.props.store.createSshKey(this.form.values);
        tryGetAppContext()?.announce.fire('SSH key created');
        this.props.onClose(result);
    }
}
